import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./financeDetails.css";
import "../finance/finance.css";
import { fmt } from "../finance/finance";

const FinanceDetails = ({ API_URL }) => {
    const { requestType, orderNumber } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    const [paymentTypes, setPaymentTypes] = useState([]);
    const [kassaList, setKassaList] = useState([]); // только карты
    const [currencies, setCurrencies] = useState([]);
    const [paymentTypeId, setPaymentTypeId] = useState(null);
    const [kassaId, setKassaId] = useState(null); // используется только для card-типов

    const [addCardOpen, setAddCardOpen] = useState(false);
    const [newCardName, setNewCardName] = useState("");
    const [newCardCurrencyId, setNewCardCurrencyId] = useState(null);
    const [savingCard, setSavingCard] = useState(false);

    const [amount, setAmount] = useState("");
    const [convRate, setConvRate] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [recreating, setRecreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getFinanceOrderDetail`, { orderNumber, requestType });
            if (data.status === "success") {
                setOrder(data.order);
                setPayments(data.payments);
            }
        } catch (err) {
            console.error("getFinanceOrderDetail:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL, orderNumber, requestType]);

    useEffect(() => { load(); }, [load]);

    const loadKassaData = useCallback(async () => {
        try {
            const [ptRes, kassaRes, curRes] = await Promise.all([
                axios.post(`${API_URL}/getPaymentTypes`),
                axios.post(`${API_URL}/getKassaList`),
                axios.post(`${API_URL}/getCurrencies`),
            ]);
            if (ptRes.data.status === "success") {
                setPaymentTypes(ptRes.data.paymentTypes);
                setPaymentTypeId((prev) => prev ?? ptRes.data.paymentTypes[0]?.id ?? null);
            }
            if (kassaRes.data.status === "success") setKassaList(kassaRes.data.kassaList);
            if (curRes.data.status === "success") {
                setCurrencies(curRes.data.currencies);
                setNewCardCurrencyId((prev) => prev ?? curRes.data.currencies[0]?.id ?? null);
            }
        } catch (err) {
            console.error("loadKassaData:", err);
        }
    }, [API_URL]);

    useEffect(() => { loadKassaData(); }, [loadKassaData]);

    const selectedType = paymentTypes.find((pt) => pt.id === paymentTypeId);
    const cardsForType = selectedType?.isCard ? kassaList.filter((k) => k.paymentTypeId === paymentTypeId && k.showInIncome) : [];

    // Валюта платежа: для обычного типа — его собственная, для card-типа — валюта выбранной карты
    const activeCurrencyCode = selectedType?.isCard
        ? (kassaList.find((k) => k.id === kassaId)?.currencyCode ?? null)
        : (selectedType?.currencyCode ?? null);
    const activeCurrencyName = selectedType?.isCard
        ? (kassaList.find((k) => k.id === kassaId)?.currencyName ?? null)
        : (selectedType?.currencyName ?? null);

    useEffect(() => {
        if (!selectedType?.isCard) { setKassaId(null); return; }
        if (!cardsForType.find((k) => k.id === kassaId)) setKassaId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentTypeId, kassaList]);

    const handleTypeChange = (id) => {
        setPaymentTypeId(id);
        setKassaId(null);
        setAddCardOpen(false);
    };

    const submitNewCard = async () => {
        if (!newCardName.trim()) { toast.error("Укажите получателя — чья карта"); return; }
        setSavingCard(true);
        try {
            await axios.post(`${API_URL}/addKassa`, {
                paymentTypeId: selectedType.id,
                currencyId: newCardCurrencyId,
                recipient: newCardName.trim(),
            });
            toast.success("Карта добавлена");
            setNewCardName("");
            setAddCardOpen(false);
            const { data } = await axios.post(`${API_URL}/getKassaList`);
            if (data.status === "success") {
                setKassaList(data.kassaList);
                const added = data.kassaList.find((k) => k.paymentTypeId === selectedType.id && k.recipient === newCardName.trim());
                if (added) setKassaId(added.id);
            }
        } catch (err) {
            console.error("addKassa:", err);
            toast.error("Не удалось добавить карту");
        } finally {
            setSavingCard(false);
        }
    };

    const convUsd = useMemo(() => {
        if (!activeCurrencyCode || activeCurrencyCode === "USD") return Number(amount) || 0;
        const amt = Number(amount);
        const rate = Number(convRate);
        if (!amt || !rate) return 0;
        return amt / rate;
    }, [amount, convRate, activeCurrencyCode]);

    const submitPayment = async () => {
        if (!paymentTypeId) { toast.error("Выберите тип оплаты"); return; }
        if (selectedType?.isCard && !kassaId) { toast.error("Выберите карту"); return; }
        if (!amount || Number(amount) <= 0) { toast.error("Укажите сумму прихода"); return; }
        if (activeCurrencyCode !== "USD" && !convRate) { toast.error(`Укажите курс для конвертации ${activeCurrencyCode} → USD`); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/addFinancePayment`, {
                orderNumber, requestType, paymentTypeId,
                kassaId: selectedType?.isCard ? kassaId : null,
                amount,
                exchangeRate: activeCurrencyCode !== "USD" ? convRate : null,
            });
            setAmount("");
            setConvRate("");
            toast.success("Приход добавлен");
            await load();
        } catch (err) {
            console.error("addFinancePayment:", err);
            toast.error("Не удалось сохранить приход");
        } finally {
            setSubmitting(false);
        }
    };

    // Кнопка "Пересоздать счёт-фактуру" — только для заказов партнёров (у них вообще
    // есть счета-фактуры), когда в форме прихода выбран тип оплаты «Перечисление»,
    // и по заказу ещё остался долг.
    const showRecreateInvoice =
        order?.requestType === "partners" &&
        selectedType?.name === "Перечисление" &&
        order?.debtAmount > 0;

    const recreateInvoice = async () => {
        setRecreating(true);
        try {
            const { data } = await axios.post(`${API_URL}/recreateFinanceTransfer`, { orderNumber });
            if (data.status === "success") {
                toast.success(`Счёт-фактура пересоздан на остаток $${fmt(data.remaining)} — старый отмечен как «Изменён»`);
            } else {
                toast.error(data.message || "Не удалось пересоздать счёт-фактуру");
            }
        } catch (err) {
            console.error("recreateFinanceTransfer:", err);
            toast.error("Не удалось пересоздать счёт-фактуру");
        } finally {
            setRecreating(false);
        }
    };

    if (loading || !order) return (
        <div className="finance">
            <button className="fn-back" onClick={() => navigate(-1)}><i className="fa-solid fa-chevron-left"></i> Назад</button>
            <div className="fn-empty">Загрузка…</div>
        </div>
    );

    const dateStartLabel = order.dateStart
        ? new Date(order.dateStart).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" })
        : "—";
    return (
        <div className="finance">
            <button className="fn-back" onClick={() => navigate(-1)}><i className="fa-solid fa-chevron-left"></i> Назад</button>

            <div className="fn-detail-top">
                <div className="fn-detail-title">Заказ № {order.orderNumber}</div>
            </div>

            <div className="fn-section">
                <div className="fn-section-head">☰ Заказчик</div>
                <div className="fn-section-body fn-fgrid">
                    {order.requestType === "partners" ? (
                        <>
                            <div className="fn-field"><label>Компания</label><input value={order.company || "—"} disabled /></div>
                            <div className="fn-field"><label>Заказчик</label><input value={order.title || "—"} disabled /></div>
                            <div className="fn-field"><label>Оператор</label><input value={order.manager || "—"} disabled /></div>
                            <div className="fn-field"><label>Тур</label><input value={order.tour || "—"} disabled /></div>
                            <div className="fn-field"><label>Дата тура</label><input value={dateStartLabel} disabled /></div>
                            <div className="fn-field"><label>Человек</label><input value={order.participantsCount || "—"} disabled /></div>
                        </>
                    ) : order.requestType === "sharetour" ? (
                        <>
                            <div className="fn-field"><label>Тип заказа</label><input value="Share Tour" disabled /></div>
                            <div className="fn-field"><label>Программа</label><input value={order.tour || "—"} disabled /></div>
                            <div className="fn-field"><label>Оператор</label><input value={order.manager || "—"} disabled /></div>
                            <div className="fn-field"><label>Дата тура</label><input value={dateStartLabel} disabled /></div>
                            <div className="fn-field"><label>Человек</label><input value={order.participantsCount || "—"} disabled /></div>
                        </>
                    ) : (
                        <>
                            <div className="fn-field"><label>Заказчик</label><input value={order.title || "—"} disabled /></div>
                            <div className="fn-field"><label>Программа</label><input value={order.tour || "—"} disabled /></div>
                            <div className="fn-field"><label>Оператор</label><input value={order.manager || "—"} disabled /></div>
                            <div className="fn-field"><label>Дата тура</label><input value={dateStartLabel} disabled /></div>
                            <div className="fn-field"><label>Человек</label><input value={order.participantsCount || "—"} disabled /></div>
                        </>
                    )}
                </div>
            </div>

            <div className="fn-section">
                <div className="fn-section-head">✎ Приход оплаты</div>
                <div className="fn-section-body">
                    <div className="fn-hint" style={{ marginTop: 0 }}>Тип оплаты</div>
                    <div className="fn-paytype">
                        {paymentTypes.filter((pt) => pt.showInIncome).map((pt) => (
                            <button key={pt.id} className={paymentTypeId === pt.id ? "selected" : ""} onClick={() => handleTypeChange(pt.id)}>
                                {pt.name}
                            </button>
                        ))}
                    </div>

                    {selectedType?.isCard && (
                        <>
                            <div className="fn-hint">Карта</div>
                            {cardsForType.length > 0 && (
                                <div className="fn-paytype">
                                    {cardsForType.map((k) => (
                                        <button key={k.id} className={kassaId === k.id ? "selected" : ""} onClick={() => setKassaId(k.id)}>
                                            {k.recipient}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {showRecreateInvoice && (
                        <div className="fn-recreate-invoice-box">
                            <div className="fn-hint" style={{ margin: 0 }}>
                                Остаток по заказу — ${fmt(order.debtAmount)}. Можно пересоздать счёт-фактуру на эту сумму:
                                старый будет отмечен как «Изменён», рядом появится новый.
                            </div>
                            <button className="fn-btn-recreate" onClick={recreateInvoice} disabled={recreating}>
                                {recreating ? "Пересоздаём…" : "Пересоздать счёт-фактуру на оставшуюся сумму"}
                            </button>
                        </div>
                    )}

                    <div className="fn-field" style={{ marginTop: 14 }}>
                        <label>Сумма {activeCurrencyCode && activeCurrencyCode !== "USD" ? `(в ${activeCurrencyCode})` : ""}</label>
                        <input placeholder="0.00" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </div>

                    {activeCurrencyCode && activeCurrencyCode !== "USD" && (
                        <div className="fn-convert-panel">
                            <div className="fn-convert-note">
                                Касса ведётся в {activeCurrencyName} — укажите курс, чтобы система пересчитала приход в доллары для заказа. Баланс кассы пополнится в её родной валюте.
                            </div>
                            <div className="fn-fgrid two">
                                <div className="fn-field"><label>Курс (1 USD = )</label><input placeholder="12 700" type="number" value={convRate} onChange={(e) => setConvRate(e.target.value)} /></div>
                                <div className="fn-field"><label>Итого в USD</label><input value={convUsd ? convUsd.toFixed(2) : ""} readOnly /></div>
                            </div>
                        </div>
                    )}

                    <button className="fn-btn-add-payment" onClick={submitPayment} disabled={submitting}>
                        {submitting ? "Сохраняем…" : "＋ Добавить приход"}
                    </button>
                </div>
            </div>

            <div className="fn-section">
                <div className="fn-section-head">§ История платежей</div>
                <div className="fn-section-body">
                    {payments.length === 0 ? (
                        <div className="fn-empty">Приходов пока нет</div>
                    ) : (
                        <table className="fn-ledger">
                            <thead>
                                <tr><th>Дата</th><th>Касса</th><th>Оплачено</th><th style={{ textAlign: "right" }}>Итого (USD)</th></tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => (
                                    <tr key={p.id} className={p.isAnnulled ? "fn-ledger-row--annulled" : ""}>
                                        <td>
                                            {p.isAnnulled && <span className="fn-annul-tag">Аннулировано</span>}
                                            {new Date(p.date).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" })}
                                            {p.createdAt && (
                                                <span className="fn-ledger-time">
                                                    {new Date(p.createdAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                            )}
                                        </td>
                                        <td>{p.kassaName}</td>
                                        <td>
                                            {p.currency === "USD" ? `$${fmt(p.amountOriginal)}` : `${fmt(p.amountOriginal)} ${p.currency}`}
                                            {p.currency !== "USD" && <span className="fn-badge-converted">⇄ конвертировано · курс {fmt(p.exchangeRate)}</span>}
                                            {p.isAnnulled && p.annulComment && <div className="fn-annul-comment">Причина: {p.annulComment}</div>}
                                        </td>
                                        <td className="num">${fmt(p.amountUsd)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className={`fn-summary-bar${order.debtAmount <= 0 ? " zero" : ""}`} style={{ marginTop: 16 }}>
                        <span className="fn-lbl">Остаток долга по заказу</span>
                        <span className="fn-val">${fmt(order.debtAmount)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinanceDetails;